import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { getUserFromLocalstrorage, isStaff } from '../helpers/global';
import { groupByCounty, getOptionsFromRecords } from '../components/CountyMunicipality/countyMunicipalityData';

const MunicipalityCountyContext = createContext(null);

const clearAppChoicesCache = () => {
  try {
    localStorage.removeItem('app-choices');
  } catch (e) {
    /* ignore */
  }
};

export const MunicipalityCountyProvider = ({ children }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const inFlightRef = useRef(null);

  const refreshRecords = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    if (!silent) setLoading(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.COUNTY_MUNICIPALITIES_SEARCH,
        method: 'POST',
        requestBody: { pageSize: 5000, pageNumber: 1 }
      });
      if (error) {
        toast.error(error || 'Failed to load locations');
        return { error };
      }
      const next = response?.results || [];
      setRecords(next);
      hasLoadedRef.current = true;

      return { results: next };
    } finally {
      if (!silent) setLoading(false);
      inFlightRef.current = null;
    }
  }, []);

  const ensureRecordsLoaded = useCallback(async () => {
    if (hasLoadedRef.current) return { results: records };
    if (inFlightRef.current) return inFlightRef.current;
    const request = refreshRecords();
    inFlightRef.current = request;
    return request;
  }, [records, refreshRecords]);


  const mergeRecord = useCallback(rec => {
    if (!rec) return;
    setRecords(prev => [...prev, rec]);
    clearAppChoicesCache();
  }, []);

  const patchRecords = useCallback(updater => {
    setRecords(prev => (typeof updater === 'function' ? updater(prev) : updater));
    clearAppChoicesCache();
  }, []);

  const handleAddCountyRecord = useCallback(
    async county => {
      const trimmed = county?.trim();
      if (!trimmed) {
        toast.error('County name is required');
        return { error: 'County name is required' };
      }
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CREATE_COUNTY_MUNICIPALITY,
        method: 'POST',
        requestBody: { county: trimmed }
      });

      if (error) {
        toast.error(error || 'Failed to add county');
        return { error };
      }

      toast.success('County added successfully');
      if (response) mergeRecord(response);
      return { response };
    },
    [mergeRecord]
  );

  const handleAddMunicipalityRecord = useCallback(
    async (county, city) => {
      const c = county?.trim();
      const t = (city ?? '').trim();
      if (!c) {
        toast.warning('County is required for municipality/city');
        return { error: 'County is required for municipality/city' };
      }
      if (!t) {
        toast.warning('Municipality/City is required');
        return { error: 'Municipality/City is required' };
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CREATE_COUNTY_MUNICIPALITY,
        method: 'POST',
        requestBody: { county: c, city: t }
      });

      if (error) {
        toast.error(error || 'Failed to add municipality/city');
        return { error };
      }

      toast.success('Municipality/City added successfully');
      if (response) mergeRecord(response);
      return { response };
    },
    [mergeRecord]
  );

  const handleDeleteCountyRecord = useCallback(
    async countyName => {
      const countyToDelete = countyName?.trim();
      if (!countyToDelete) {
        return { error: 'County is required' };
      }
      const countyRecords = records.filter(r => r.county === countyToDelete);
      let failed = 0;
      for (const r of countyRecords) {
        const { error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.COUNTY_MUNICIPALITY_WITH_ID(r._id),
          method: 'DELETE'
        });
        if (error) failed++;
      }
      if (failed > 0) {
        toast.error(`Failed to delete ${failed} record(s)`);
        return { error: true };
      }
      patchRecords(prev => prev.filter(r => r.county !== countyToDelete));
      toast.success('County removed.');
      return {};
    },
    [records, patchRecords]
  );

  const handleDeleteMunicipalityRecord = useCallback(
    async recordId => {
      if (!recordId) {
        return { error: 'Record id is required' };
      }
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.COUNTY_MUNICIPALITY_WITH_ID(recordId),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error || 'Failed to delete');
        return { error };
      }
      patchRecords(prev => prev.filter(r => r._id !== recordId));
      toast.success('Municipality removed.');
      return {};
    },
    [patchRecords]
  );

  const handleAddCityRecord = useCallback(
    async (county, countyCity) => {
      const c = county?.trim();
      const t = (countyCity ?? '').trim();
      if (!c) {
        toast.warning('County is required for city');
        return { error: 'County is required for city' };
      }
      if (!t) {
        toast.warning('City name is required');
        return { error: 'City name is required' };
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.CREATE_COUNTY_MUNICIPALITY,
        method: 'POST',
        requestBody: { county: c, countyCity: t }
      });

      if (error) {
        toast.error(error || 'Failed to add city');
        return { error };
      }

      toast.success('City added successfully');
      if (response) mergeRecord(response);
      return { response };
    },
    [mergeRecord]
  );

  const handleDeleteCityRecord = useCallback(
    async recordId => {
      if (!recordId) {
        return { error: 'Record id is required' };
      }
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.COUNTY_MUNICIPALITY_WITH_ID(recordId),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error || 'Failed to delete city');
        return { error };
      }
      patchRecords(prev => prev.filter(r => r._id !== recordId));
      toast.success('City removed.');
      return {};
    },
    [patchRecords]
  );

  const { countyNames, municipalityNames, cityNames } = useMemo(() => getOptionsFromRecords(records), [records]);

  const countiesGrouped = useMemo(() => groupByCounty(records), [records]);

  const municipalityOptions = useMemo(
    () => municipalityNames.map(city => ({ label: city, value: city })),
    [municipalityNames]
  );

  const countyOptions = useMemo(
    () => countyNames.map(c => ({ label: c, value: c })),
    [countyNames]
  );

  const value = useMemo(
    () => ({
      records,
      loading,
      countyNames,
      municipalityNames,
      cityNames,
      allCities: municipalityNames,
      countiesGrouped,
      municipalityOptions,
      countyOptions,
      ensureRecordsLoaded,
      refreshRecords,
      mergeRecord,
      patchRecords,
      handleAddCountyRecord,
      handleAddMunicipalityRecord,
      handleDeleteCountyRecord,
      handleDeleteMunicipalityRecord,
      handleAddCityRecord,
      handleDeleteCityRecord
    }),
    [
      records,
      loading,
      countyNames,
      municipalityNames,
      cityNames,
      countiesGrouped,
      municipalityOptions,
      countyOptions,
      ensureRecordsLoaded,
      refreshRecords,
      mergeRecord,
      patchRecords,
      handleAddCountyRecord,
      handleAddMunicipalityRecord,
      handleDeleteCountyRecord,
      handleDeleteMunicipalityRecord,
      handleAddCityRecord,
      handleDeleteCityRecord
    ]
  );

  return (
    <MunicipalityCountyContext.Provider value={value}>{children}</MunicipalityCountyContext.Provider>
  );
};

const useMunicipalityCounty = ({ isEnabled = true } = {}) => {
  const ctx = useContext(MunicipalityCountyContext);
  if (!ctx) {
    throw new Error('useMunicipalityCounty must be used within MunicipalityCountyProvider');
  }

  const shouldFetch = isEnabled && isStaff(getUserFromLocalstrorage()?.role);

  // ensure records are loaded only when hook is used by staff
  useEffect(() => {
    if (shouldFetch) {
      ctx.ensureRecordsLoaded?.();
    }
  }, [ctx, shouldFetch]);
  return ctx;
};

export default useMunicipalityCounty;
